import { useCallback, useEffect, useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import {
  dateCourte, envoyerSymphonics, getEnvoisClient, raccorderSurvoltage, renvoyerSurvoltage,
} from '@/shared/api/bridge';
import { etatSurvoltage, etatSymphonics } from '@/features/bridge/envois-etat';
import ErreurBloc from '@/features/bridge/components/ErreurBloc';

/**
 * L'envoi du dossier de ce client à chaque agrégateur, depuis sa fiche (06/10/2026).
 *
 * POURQUOI CE BLOC EXISTE
 *
 * Déclarer un actif (le bloc du dessus) ne fait rien partir. Les deux envois existaient
 * sans déclencheur : la route Symphonics n'était appelée par aucun écran, et l'envoi
 * Survoltage n'avait ni route ni appelant. Les agrégateurs ne recevaient donc rien.
 *
 * SYMPHONICS : APERÇU PUIS ENVOI
 *
 * Le premier clic est un aperçu (`dry_run`) : il vérifie la pièce signée et liste les
 * actifs qui partiraient, sans rien transmettre. L'envoi réel se confirme ensuite DANS
 * le bloc, jamais par une fenêtre du navigateur (bloquée dans certains contextes, et
 * qui ne montre pas ce qui va partir).
 *
 * SURVOLTAGE : RACCORDER UNE FOIS, PUIS LE RENVOI SUIT SEUL
 *
 * Raccorder crée le client chez eux avec ses actifs. Ensuite, chaque déclaration ou
 * retrait d'un actif Survoltage leur renvoie la liste automatiquement ; « Renvoyer »
 * sert après une panne, quand la dernière erreur s'affiche ici.
 */

const TEINTE = {
  ok: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  attente: 'border-neutral-200 bg-neutral-50 text-neutral-700',
  bloque: 'border-amber-200 bg-amber-50 text-amber-800',
  erreur: 'border-red-200 bg-red-50 text-red-800',
};

function Ligne({ titre, etat, testid, children }) {
  return (
    <div className="rounded-md border border-neutral-200 p-3 space-y-2" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[13px] font-medium text-neutral-800">{titre}</span>
        <div className="flex flex-wrap gap-2">{children}</div>
      </div>
      <p className={`rounded-md border px-2.5 py-1.5 text-[12px] ${TEINTE[etat.ton]}`}
         data-testid={`${testid}-etat`}>
        {etat.texte}
      </p>
    </div>
  );
}

export default function EnvoisAgregateurs({ clientId }) {
  const [envois, setEnvois] = useState(null);
  const [apercu, setApercu] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState('');

  const charger = useCallback(async () => {
    try {
      setEnvois(await getEnvoisClient(clientId));
    } catch (exc) {
      setErreur(exc);
    }
  }, [clientId]);

  useEffect(() => { charger(); }, [charger]);

  const agir = async (cle, geste) => {
    setEnCours(cle);
    setErreur(null);
    try {
      return await geste();
    } catch (exc) {
      // Un refus metier (pas de piece signee, aucun actif declare) arrive en 4xx avec sa
      // raison : l afficher comme un refus, pas comme un « erreur de chargement ».
      // Un 403 garde son message d acces : c est un autre sujet qu un refus d envoi.
      const raison = [400, 404, 409, 422].includes(exc?.response?.status)
        && exc?.response?.data?.detail;
      setErreur(raison ? `Refusé : ${raison}` : exc);
      return null;
    } finally {
      setEnCours('');
      await charger();
    }
  };

  if (!envois) {
    return (
      <Card data-testid="client-envois">
        <CardContent className="pt-5 text-sm text-neutral-500">
          {erreur ? <ErreurBloc erreur={erreur} /> : 'Lecture des envois…'}
        </CardContent>
      </Card>
    );
  }

  const sym = etatSymphonics(envois.symphonics);
  const sur = etatSurvoltage(envois.survoltage);
  const occupe = Boolean(enCours);

  return (
    <Card data-testid="client-envois">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Send className="w-4 h-4" /> Envoi aux agrégateurs
        </CardTitle>
        <p className="text-[13px] text-neutral-500 leading-relaxed max-w-3xl">
          Seuls les actifs déclarés ci-dessus partent, jamais tout le dossier du client.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <ErreurBloc erreur={erreur} testid="envois-erreur" />

        <Ligne titre="Symphonics" etat={sym} testid="envoi-symphonics">
          <Button size="sm" variant="outline" disabled={!sym.peutEnvoyer || occupe}
                  data-testid="symphonics-apercu"
                  onClick={async () => setApercu(
                    await agir('apercu', () => envoyerSymphonics(clientId, true)))}>
            {enCours === 'apercu' ? 'Vérification…' : 'Aperçu de l’envoi'}
          </Button>
        </Ligne>

        {apercu && (
          <div className="rounded-md border border-neutral-300 p-3 space-y-2 text-[12px]"
               data-testid="symphonics-confirmation">
            <p className="font-medium text-neutral-800">Ce qui partira chez Symphonics</p>
            <ul className="list-disc pl-5 text-neutral-700 space-y-0.5">
              <li>{(apercu.payload?.installed_devices || []).length} équipement(s) déclaré(s)</li>
              <li>La pièce signée ({apercu.upload?.origine_pdf || 'origine inconnue'},
                {' '}{Math.round((apercu.upload?.size_bytes || 0) / 1024)} Ko)</li>
              <li>La fiche du client : identité, adresse, contact, point de livraison</li>
            </ul>
            <div className="flex gap-2">
              <Button size="sm" disabled={occupe} data-testid="symphonics-envoyer"
                      onClick={async () => {
                        await agir('envoi', () => envoyerSymphonics(clientId, false));
                        setApercu(null);
                      }}>
                {enCours === 'envoi' ? 'Envoi…' : 'Envoyer le dossier à Symphonics'}
              </Button>
              <Button size="sm" variant="ghost" disabled={occupe}
                      onClick={() => setApercu(null)}>
                Annuler
              </Button>
            </div>
          </div>
        )}

        <Ligne titre="Survoltage" etat={sur} testid="envoi-survoltage">
          {sur.action === 'raccorder' && (
            <Button size="sm" variant="outline" disabled={occupe}
                    data-testid="survoltage-raccorder"
                    onClick={() => agir('raccorder', () => raccorderSurvoltage(clientId))}>
              {enCours === 'raccorder' ? 'Raccordement…' : 'Raccorder chez Survoltage'}
            </Button>
          )}
          {sur.action === 'renvoyer' && (
            <Button size="sm" variant="outline" disabled={occupe}
                    data-testid="survoltage-renvoyer"
                    onClick={() => agir('renvoyer', () => renvoyerSurvoltage(clientId))}>
              {enCours === 'renvoyer' ? 'Envoi…' : 'Renvoyer les actifs'}
            </Button>
          )}
        </Ligne>
        {envois.survoltage?.dernier_envoi && (
          <p className="text-[11px] text-neutral-500">
            Survoltage : dernier envoi {dateCourte(envois.survoltage.dernier_envoi)}
            {envois.survoltage.uid ? ` · client ${envois.survoltage.uid}` : ''}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
